import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Zap, 
  Video, 
  Calendar, 
  BarChart, 
  Palette,
  MessageSquare,
  CheckCircle,
  Sparkles,
  Flame,
  Award,
  Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ConsistencyLogo from '@/components/ConsistencyLogo';
import { useLegalLinks, getLegalUrl } from '@/hooks/useLegalLinks';

const API_URL = window.__BACKEND_URL;
const defaultSettings = {
  demo_video_url: '',
  show_demo_video: false
};

export default function LandingPage() {
  const [settings, setSettings] = useState(defaultSettings);
  const { documents: footerLinks } = useLegalLinks('footer');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/landing-page`);
        if (res.ok) {
          const data = await res.json();
          setSettings({ ...defaultSettings, ...(data || {}) });
        }
      } catch (err) {
        // fallback to defaults
      }
    };
    loadSettings();
  }, []);

  return (
    <div className="min-h-screen bg-[#121417]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <ConsistencyLogo variant="wordmark" size="md" />
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-pink-500 hover:bg-pink-600">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-pink-500/10 text-pink-400 rounded-full text-sm font-medium mb-6">
              <Sparkles className="inline h-4 w-4 mr-2" />
              Create Freely
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Turn Your Lives Into a
            <br />
            <span className="bg-gradient-to-r from-[#3A7BFF] to-[#39E6C8] bg-clip-text text-transparent">
              Full Content Machine
            </span>
          </h1>
          
          {/* Hero Subheadline - 3 Bullets */}
          <div className="mb-8 max-w-2xl mx-auto">
            <ul className="space-y-2 text-lg text-[#8A93A6]">
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>Go live once</span>
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>Clips, scheduling, and posting handled automatically</span>
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span>AI-powered, approval-first</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button size="lg" className="bg-pink-500 hover:bg-pink-600 w-full sm:w-auto">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {settings.show_demo_video && settings.demo_video_url && (
        <section className="pb-10 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Video className="h-5 w-5 text-pink-400" />
                <p className="text-white font-semibold">Product Demo</p>
              </div>
              <div className="aspect-video bg-black/40 border border-white/10 rounded-xl overflow-hidden">
                <video src={settings.demo_video_url} controls className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 px-4 bg-white/[0.02]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Your Content Pipeline, Automated
            </h2>
            <p className="text-lg text-[#8A93A6] max-w-2xl mx-auto">
              From live stream to scheduled posts—all in one workflow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Video className="h-6 w-6 text-pink-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">1. Go Live or Upload</h3>
                <p className="text-[#8A93A6]">
                  Stream on your favorite platform or upload existing content. We capture everything automatically.
                </p>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">2. AI Creates Clips</h3>
                <p className="text-[#8A93A6]">
                  Our AI engine automatically generates short-form and long-form clips, ready for your review.
                </p>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">3. Autopilot Posts</h3>
                <p className="text-[#8A93A6]">
                  Review and approve your content. Autopilot handles scheduling and posting across all platforms—with your permission.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Everything You Need to Stay Consistent
            </h2>
            <p className="text-lg text-[#8A93A6]">
              A complete toolkit for content creators
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="glass">
                <CardContent className="pt-6">
                  <feature.icon className={`h-8 w-8 mb-4 ${feature.color}`} />
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#8A93A6]">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="py-20 px-4 bg-white/[0.02]">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Trusted by Creators Who Ship
            </h2>
            <p className="text-lg text-[#8A93A6]">
              Join hundreds of creators automating their content
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="glass">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.initials}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-[#8A93A6]">{testimonial.type}</p>
                    </div>
                  </div>
                  <p className="text-[#8A93A6] italic">&ldquo;{testimonial.quote}&rdquo;</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="glass border-pink-500/20">
            <CardContent className="pt-8 pb-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Create Freely?
              </h2>
              <p className="text-lg text-[#8A93A6] mb-6">
                Start your free trial and transform how you create content
              </p>
              <Link to="/pricing">
                <Button size="lg" className="bg-pink-500 hover:bg-pink-600">
                  Start Free Trial
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <ConsistencyLogo variant="symbol" size="md" className="mb-4" />
              <p className="text-sm text-[#8A93A6]">
                Transform your live streams into a full content machine.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="text-[#8A93A6] hover:text-white">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                {footerLinks.length > 0 ? (
                  footerLinks.map((doc) => (
                    <li key={doc.slug}>
                      <Link to={getLegalUrl(doc.slug)} className="text-[#8A93A6] hover:text-white">
                        {doc.title}
                      </Link>
                    </li>
                  ))
                ) : (
                  <li className="text-[#8A93A6] opacity-50">No legal documents</li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="text-[#8A93A6] hover:text-white">Help Center</Link></li>
                <li>
                  <span className="text-[#8A93A6] opacity-50 cursor-not-allowed">Support Form</span>
                  <span className="block text-xs text-[#8A93A6] mt-1">(Coming soon)</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-[#8A93A6]">
            <p>© 2025 Consistency. Create Freely.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Feature data
const features = [
  {
    icon: Video,
    title: 'Live Capture',
    description: 'Automatic recording and processing of your live streams across platforms',
    color: 'text-pink-500'
  },
  {
    icon: Zap,
    title: 'AI Clip Engine',
    description: 'Smart clip generation for both short-form and long-form content',
    color: 'text-blue-500'
  },
  {
    icon: Calendar,
    title: 'Smart Scheduler',
    description: 'Schedule posts across all platforms with optimal timing',
    color: 'text-green-500'
  },
  {
    icon: Sparkles,
    title: 'Autopilot (Core)',
    description: 'Approval-first automated posting—you review, AI handles the rest',
    color: 'text-yellow-500'
  },
  {
    icon: Zap,
    title: 'Advanced Autopilot (Pro+)',
    description: 'Streak protection, auto-ads copy, and auto-rebrand capabilities',
    color: 'text-orange-500'
  },
  {
    icon: Palette,
    title: 'BrandKit',
    description: 'Apply your branding automatically to every piece of content',
    color: 'text-purple-500'
  },
  {
    icon: BarChart,
    title: 'Analytics',
    description: 'Track performance and growth across all your platforms',
    color: 'text-cyan-500'
  },
  {
    icon: MessageSquare,
    title: 'Community Manager',
    description: 'Manage comments and engagement across all platforms',
    color: 'text-indigo-500'
  },
  {
    icon: CheckCircle,
    title: 'Multi-Platform',
    description: 'YouTube, TikTok, Instagram, and more—all in one place',
    color: 'text-green-400'
  },
  {
    icon: Flame,
    title: 'Streaks & Badges',
    description: 'Track your consistency and earn achievement badges',
    color: 'text-orange-400'
  },
  {
    icon: Archive,
    title: 'Auto-Archive Content Lifecycle',
    description: 'Automated content organization and lifecycle management',
    color: 'text-gray-400'
  }
];

// Testimonial data
const testimonials = [
  {
    initials: 'JD',
    name: 'Jordan Davis',
    type: 'Individual Creator',
    quote: 'Consistency transformed my workflow. I go live once and get weeks of content without the manual grind.'
  },
  {
    initials: 'SM',
    name: 'Sarah Mitchell',
    type: 'Agency Owner',
    quote: 'Managing 15+ creators used to be chaos. Consistency gives us structure and automation that actually works.'
  },
  {
    initials: 'MR',
    name: 'Marcus Roberts',
    type: 'Individual Creator',
    quote: 'The AI clip engine is incredible. It finds the best moments and brands them perfectly—I just approve and post.'
  },
  {
    initials: 'AL',
    name: 'Aisha Lopez',
    type: 'Individual Creator',
    quote: 'I finally have time to focus on creating instead of editing and scheduling. Autopilot is a game-changer.'
  }
];
